import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  Paper,
  Divider,
  Tooltip,
  Snackbar,
  IconButton,
} from '@mui/material';
import { useAxios } from '../context/AxiosContext';
import LoadingSpinner from '../components/LoadingSpinner';

const Dashboard = () => {
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingServer, setEditingServer] = useState(null);
  const [formData, setFormData] = useState({
    server_id: '',
    server_name: '',
  });
  const [errors, setErrors] = useState({});
  const [alert, setAlert] = useState({ show: false, message: '', severity: 'error' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const { getServers, addServer, updateServer, deleteServer, user } = useAxios();

  // Check if user already has a server
  const hasServer = servers.length > 0;
  const currentServer = servers[0];

  useEffect(() => {
    loadServers();
  }, []);

  const loadServers = async () => {
    setLoading(true);
    try {
      const result = await getServers();
      if (result.success) {
        setServers(result.data.servers || []);
      } else {
        setAlert({
          show: true,
          message: result.error || 'Failed to load servers',
          severity: 'error'
        });
      }
    } catch (error) {
      setAlert({
        show: true,
        message: 'An unexpected error occurred',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (server = null) => {
    setEditingServer(server);
    setFormData({
      server_id: server?.server_id || '',
      server_name: server?.server_name || '',
    });
    setErrors({});
    setAlert({ show: false, message: '', severity: 'error' });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingServer(null);
    setFormData({ server_id: '', server_name: '' });
    setErrors({});
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.server_id) {
      newErrors.server_id = 'Server ID is required';
    } else if (!/^\d+$/.test(formData.server_id)) {
      newErrors.server_id = 'Server ID must be numeric';
    } else if (formData.server_id.length > 19) {
      newErrors.server_id = 'Server ID must be 19 digits or less';
    }
    
    return newErrors;
  };

  const handleSubmit = async () => {
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    setErrors({});
    setAlert({ show: false, message: '', severity: 'error' });

    try {
      let result;
      if (editingServer) {
        result = await updateServer(editingServer.server_id, {
          server_name: formData.server_name,
        });
      } else {
        result = await addServer(formData.server_id, formData.server_name);
      }

      if (result.success) {
        setSnackbar({
          open: true,
          message: editingServer ? '✅ Server updated successfully!' : '🎉 Server connected successfully!'
        });
        handleCloseDialog();
        loadServers();
      } else {
        setAlert({
          show: true,
          message: result.error || 'Operation failed',
          severity: 'error'
        });
      }
    } catch (error) {
      setAlert({
        show: true,
        message: 'An unexpected error occurred',
        severity: 'error'
      });
    }
  };

  const handleDelete = async (serverId) => {
    if (!window.confirm('⚠️ Are you sure you want to disconnect this server? This action cannot be undone.')) {
      return;
    }

    try {
      const result = await deleteServer(serverId);
      if (result.success) {
        setSnackbar({
          open: true,
          message: '🗑️ Server disconnected successfully!'
        });
        loadServers();
      } else {
        setAlert({
          show: true,
          message: result.error || 'Failed to disconnect server',
          severity: 'error'
        });
      }
    } catch (error) {
      setAlert({
        show: true,
        message: 'An unexpected error occurred',
        severity: 'error'
      });
    }
  };

  const getEmbedCode = (serverId) => {
    return `<iframe src="https://ai-bet.online/iframe?channelId=YOUR_CHANNEL_ID&serverId=${serverId}" width="100%" height="500" style="border: none;" title="Discord Chat"></iframe>`;
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      setSnackbar({
        open: true,
        message: `📋 ${label} copied to clipboard!`
      });
    });
  };

  if (loading) {
    return <LoadingSpinner message="Loading your dashboard..." />;
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 6 }}>
      <Container maxWidth="md">
        <Box sx={{ mb: 6, textAlign: 'center' }}>
          <Typography 
            variant="h2" 
            gutterBottom
            sx={{
              fontWeight: 800,
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 2,
            }}
          >
            📊 Dashboard
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ lineHeight: 1.6, mb: 1 }}>
            Welcome back, <strong>{user?.email}</strong>!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ opacity: 0.8 }}>
            {hasServer ? 'Manage your Discord server integration' : 'Connect your Discord server to get started'}
          </Typography>
        </Box>

        {alert.show && (
          <Alert 
            severity={alert.severity} 
            sx={{ 
              mb: 4,
              borderRadius: 2,
              '& .MuiAlert-message': {
                fontWeight: 500,
              },
            }}
          >
            {alert.message}
          </Alert>
        )}

        {!hasServer ? (
          <Paper
            sx={{
              p: 8,
              textAlign: 'center',
              background: 'linear-gradient(145deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.9) 100%)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: 4,
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
            }}
          >
            <Typography variant="h1" sx={{ fontSize: '5rem', mb: 3, opacity: 0.7 }}>
              🏠
            </Typography>
            <Typography variant="h3" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
              Connect Your Discord Server
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, fontSize: '1.2rem', maxWidth: 500, mx: 'auto' }}>
              Connect your Discord server to start embedding interactive chat on your website. 
              <strong> One server per account.</strong>
            </Typography>
            <Button
              variant="contained"
              onClick={() => handleOpenDialog()}
              sx={{
                px: 8,
                py: 3,
                fontSize: '1.2rem',
                fontWeight: 700,
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                borderRadius: 3,
                '&:hover': {
                  background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                  transform: 'translateY(-3px)',
                  boxShadow: '0 15px 35px rgba(99, 102, 241, 0.4)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              🚀 Connect Server
            </Button>
          </Paper>
        ) : (
          <Card sx={{ 
            background: 'linear-gradient(145deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.9) 100%)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 4,
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
            overflow: 'visible',
          }}>
            <CardContent sx={{ p: 6 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
                <Box>
                  <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 2 }}>
                    🏠 {currentServer.server_name || `Discord Server`}
                    <Chip
                      label={currentServer.is_active ? '🟢 Connected' : '🔴 Disconnected'}
                      color={currentServer.is_active ? 'success' : 'default'}
                      sx={{ fontWeight: 600, fontSize: '0.9rem' }}
                    />
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                    Server ID: <code style={{ 
                      backgroundColor: 'rgba(99, 102, 241, 0.1)', 
                      padding: '4px 8px', 
                      borderRadius: '4px',
                      fontSize: '0.9rem'
                    }}>{currentServer.server_id}</code>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Connected on {new Date(currentServer.created_date).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title="Edit server settings">
                    <IconButton
                      onClick={() => handleOpenDialog(currentServer)}
                      sx={{ 
                        bgcolor: 'rgba(99, 102, 241, 0.1)',
                        color: '#6366f1',
                        '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.2)' }
                      }}
                    >
                      ✏️
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Disconnect server">
                    <IconButton
                      onClick={() => handleDelete(currentServer.server_id)}
                      sx={{ 
                        bgcolor: 'rgba(239, 68, 68, 0.1)',
                        color: '#ef4444',
                        '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.2)' }
                      }}
                    >
                      🗑️
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              <Divider sx={{ my: 4, opacity: 0.2 }} />

              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                🚀 Next Steps
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4, fontSize: '1.1rem' }}>
                Your Discord server is connected! Now follow our step-by-step guide to install the bot and get the embed code for your website.
              </Typography>

              <Box sx={{ 
                p: 4, 
                bgcolor: 'rgba(99, 102, 241, 0.1)', 
                borderRadius: 3,
                border: '2px solid rgba(99, 102, 241, 0.3)',
                textAlign: 'center',
                mb: 4
              }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#6366f1' }}>
                  📖 Continue Setup
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
                  Follow our documentation to install the bot on your Discord server and get your embed code.
                </Typography>
                                 <Button
                   variant="contained"
                   href="/docs#step-2"
                  sx={{
                    px: 6,
                    py: 2,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 10px 25px rgba(99, 102, 241, 0.3)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  📚 View Setup Guide
                </Button>
              </Box>

              

              <Box sx={{ 
                mt: 4, 
                p: 3, 
                bgcolor: 'rgba(59, 130, 246, 0.1)', 
                borderRadius: 2,
                border: '1px solid rgba(59, 130, 246, 0.2)'
              }}>
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                  💡 What's Next:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  1. Install the Wizcord bot on your Discord server<br/>
                  2. Get your channel ID from Discord<br/>
                  3. Copy the embed code and add it to your website
                </Typography>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Add/Edit Server Dialog */}
        <Dialog 
          open={openDialog} 
          onClose={handleCloseDialog} 
          maxWidth="sm" 
          fullWidth
          PaperProps={{
            sx: {
              background: 'linear-gradient(145deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.98) 100%)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: 3,
            }
          }}
        >
          <DialogTitle sx={{ fontSize: '1.5rem', fontWeight: 700 }}>
            {editingServer ? '✏️ Edit Server Settings' : '🏠 Connect Discord Server'}
          </DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            {alert.show && (
              <Alert 
                severity={alert.severity} 
                sx={{ 
                  mb: 3,
                  borderRadius: 2,
                  '& .MuiAlert-message': {
                    fontWeight: 500,
                  },
                }}
              >
                {alert.message}
              </Alert>
            )}
            
            {!editingServer && (
              <Alert 
                severity="info" 
                sx={{ 
                  mb: 3,
                  borderRadius: 2,
                  '& .MuiAlert-message': {
                    fontWeight: 500,
                  },
                }}
              >
                You can connect one Discord server per account. Choose wisely!
              </Alert>
            )}

            {!editingServer && (
              <Box sx={{ 
                mb: 3,
                p: 3,
                bgcolor: 'rgba(99, 102, 241, 0.1)',
                borderRadius: 2,
                border: '1px solid rgba(99, 102, 241, 0.3)'
              }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 2, color: '#6366f1' }}>
                  📋 How to get your Discord Server ID:
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '13px', lineHeight: 1.6, color: 'text.secondary' }}>
                  • Enable Developer Mode: User Settings → Advanced → Developer Mode<br/>
                  • Right-click on your Discord server name (in the server list)<br/>
                  • Select "Copy Server ID" from the context menu
                </Typography>
              </Box>
            )}
            
            <TextField
              margin="normal"
              required
              fullWidth
              name="server_id"
              label="Discord Server ID"
              value={formData.server_id}
              onChange={handleChange}
              error={!!errors.server_id}
              helperText={errors.server_id || 'Paste the server ID you copied from Discord'}
              disabled={!!editingServer}
              sx={{ mb: 2 }}
            />
            
            <TextField
              margin="normal"
              fullWidth
              name="server_name"
              label="Server Name (Optional)"
              value={formData.server_name}
              onChange={handleChange}
              error={!!errors.server_name}
              helperText={errors.server_name || 'A friendly name to identify your server'}
            />
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 1 }}>
            <Button 
              onClick={handleCloseDialog}
              sx={{ fontWeight: 500 }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              variant="contained"
              sx={{
                fontWeight: 600,
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                },
              }}
            >
              {editingServer ? '💾 Update Server' : '🚀 Connect Server'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Success Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ open: false, message: '' })}
          message={snackbar.message}
          sx={{
            '& .MuiSnackbarContent-root': {
              backgroundColor: 'success.main',
              color: 'white',
              fontWeight: 500,
            },
          }}
        />
      </Container>
    </Box>
  );
};

export default Dashboard; 